/**
 * Global theme JavaScript
 * — Sticky / transparent header
 * — Mobile navigation toggle
 * — Smooth scroll for anchor links
 */

(function () {
    'use strict';

    const header = document.getElementById('site-header');
    const toggle = document.getElementById('mobile-menu-toggle');
    const mobileNav = document.getElementById('mobile-nav');

    if (!header) return;

    // ── Sticky / transparent header ───────────────────────────
    //
    // The header is transparent over a hero block at page top.
    // If the first block is NOT a hero, we mark it solid immediately.
    // On scroll past 20px we always switch to solid (white + shadow).

    const firstBlock = document.querySelector('.site-content > .wp-block-post-content > *:first-child, main > *:first-child');
    const hasHeroTop = firstBlock && (
        (
            firstBlock.classList.contains('hero') &&
            !firstBlock.classList.contains('hero--split')
        ) ||
        firstBlock.classList.contains('wp-block-cover')
    );

    if (!hasHeroTop) {
        header.classList.add('is-solid');
    }

    const onScroll = () => {
        if (window.scrollY > 20) {
            header.classList.add('is-scrolled');
        } else {
            header.classList.remove('is-scrolled');
            if (!hasHeroTop) header.classList.add('is-solid');
        }
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll(); // run once on load

    // ── Mobile navigation ─────────────────────────────────────

    if (toggle && mobileNav) {
        toggle.addEventListener('click', () => {
            const isOpen = toggle.getAttribute('aria-expanded') === 'true';

            toggle.setAttribute('aria-expanded', String(!isOpen));
            mobileNav.setAttribute('aria-hidden', String(isOpen));
            mobileNav.classList.toggle('is-open', !isOpen);

            // Prevent body scroll when menu is open
            document.body.style.overflow = isOpen ? '' : 'hidden';
        });

        // ── Mobile submenu toggles ────────────────────────────
        //
        // For each menu item that has children: wrap the > a in a flex
        // container, inject a chevron toggle button, and toggle .is-open
        // on click. Parent links that go to "#" also act as toggles.

        mobileNav.querySelectorAll('.menu-item-has-children').forEach((item) => {
            const parentLink = item.querySelector(':scope > a');
            const subMenu    = item.querySelector(':scope > .sub-menu');
            if (!parentLink || !subMenu) return;

            // Wrap link + toggle button in a flex row
            const header = document.createElement('div');
            header.className = 'bp-submenu-header';
            parentLink.parentNode.insertBefore(header, parentLink);
            header.appendChild(parentLink);

            const btn = document.createElement('button');
            btn.type      = 'button';
            btn.className = 'bp-submenu-toggle';
            btn.setAttribute('aria-expanded', 'false');
            btn.setAttribute('aria-label', 'Atidaryti submeniu');
            header.appendChild(btn);

            const toggleSubmenu = () => {
                const open = item.classList.toggle('is-open');
                btn.setAttribute('aria-expanded', String(open));
            };

            btn.addEventListener('click', (e) => {
                e.stopPropagation();
                toggleSubmenu();
            });

            // If parent link href is "#", clicking text also toggles
            if (parentLink.getAttribute('href') === '#') {
                parentLink.addEventListener('click', (e) => {
                    e.preventDefault();
                    toggleSubmenu();
                });
            }
        });

        // Close mobile nav on link click.
        // Skip parent items whose link is "#" (they toggle the sub-menu instead).
        mobileNav.querySelectorAll('a').forEach((link) => {
            const isParentToggle = link.closest('.menu-item-has-children') &&
                                   link.getAttribute('href') === '#';
            if (isParentToggle) return;

            link.addEventListener('click', () => {
                toggle.setAttribute('aria-expanded', 'false');
                mobileNav.setAttribute('aria-hidden', 'true');
                mobileNav.classList.remove('is-open');
                document.body.style.overflow = '';
            });
        });

        // Close on Escape key
        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape' && mobileNav.classList.contains('is-open')) {
                toggle.setAttribute('aria-expanded', 'false');
                mobileNav.setAttribute('aria-hidden', 'true');
                mobileNav.classList.remove('is-open');
                document.body.style.overflow = '';
                toggle.focus();
            }
        });
    }

    // ── Scroll-reveal ─────────────────────────────────────────
    //
    // Every <section> inside <main> gets .js-reveal.
    // IntersectionObserver swaps it to .is-revealed once 10% of the
    // element is in the viewport, triggering the CSS fade-up transition.

    if ('IntersectionObserver' in window) {
        const revealObserver = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        entry.target.classList.add('is-revealed');
                        revealObserver.unobserve(entry.target);
                    }
                });
            },
            { threshold: 0.08, rootMargin: '0px 0px -40px 0px' }
        );

        document.querySelectorAll('main section').forEach((el) => {
            // Skip elements that are already in the viewport on first load
            // (above-the-fold content) so they appear instantly.
            const rect = el.getBoundingClientRect();
            if (rect.top < window.innerHeight * 0.92) {
                el.classList.add('is-revealed');
            } else {
                el.classList.add('js-reveal');
                revealObserver.observe(el);
            }
        });
    }

    // ── Smooth scroll for on-page anchor links ────────────────

    document.querySelectorAll('a[href^="#"]').forEach((anchor) => {
        anchor.addEventListener('click', (e) => {
            const targetId = anchor.getAttribute('href').slice(1);
            if (!targetId) return;

            const target = document.getElementById(targetId);
            if (!target) return;

            e.preventDefault();

            const headerOffset = parseInt(
                getComputedStyle(document.documentElement).getPropertyValue('--header-height') || '72',
                10
            );
            const top = target.getBoundingClientRect().top + window.scrollY - headerOffset - 16;

            window.scrollTo({ top, behavior: 'smooth' });
        });
    });

})();
